import { Directive, Input, OnInit, OnDestroy, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { GoogleTagManagerService } from '../services/gtm';
import { GtmConfigService } from '../services/gtm/gtm-config.service';
import { AuthService } from '../auth/auth.service';
import { filter, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

@Directive({
  selector: '[libGtmInitializer]',
})
export class GtmInitializerDirective implements OnInit, OnDestroy {
  @Input() autoTrackPageView = true;
  @Input() requireAuthentication = true;

  private destroy$ = new Subject<void>();
  private gtmInitialized = false;

  constructor(
    private gtmService: GoogleTagManagerService,
    private gtmConfigService: GtmConfigService,
    private authService: AuthService,
    @Inject(PLATFORM_ID) private platformId: object
  ) {}

  ngOnInit(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    if (this.requireAuthentication) {
      // Wait for user authentication before initializing GTM
      this.authService.signIn$
        .pipe(
          filter(({ success }) => success),
          takeUntil(this.destroy$)
        )
        .subscribe(() => {
          this.initializeGtm();
        });

      // Also check if user is already authenticated
      this.authService.check().subscribe((isAuthenticated) => {
        if (isAuthenticated && !this.gtmInitialized) {
          this.initializeGtm();
        }
      });
    } else {
      // Initialize GTM immediately if authentication is not required
      this.initializeGtm();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeGtm(): void {
    if (this.gtmInitialized) {
      return;
    }

    this.gtmConfigService.getGtmConfig().subscribe((config) => {
      if (config) {
        this.gtmService.initialize(config);
        this.gtmInitialized = true;

        if (this.autoTrackPageView) {
          setTimeout(() => {
            this.gtmService.trackPageView();
          }, 100);
        }
      } else {
        console.warn('Unable to get GTM configuration');
      }
    });
  }
}